<script setup>
import { ref, computed, reactive } from 'vue';
import { useStore } from 'vuex';
import { DsIcon, DsText } from '@coloplast/design-system';
import DropdownSingleSelect from 'multisite/components/formElements/dropdownSingleSelect.vue';

const props = defineProps({
	item: {
		type: Object,
		required: true,
	},
	labels: {
		type: Object,
		required: true,
	},
	accessibilityLabels: {
		type: Object,
		required: true,
	},
});

const store = useStore();
const updateCuttingService = (payload) => store.dispatch('updateCuttingService', payload);

const cuttingTemplates = ref(props.item.cuttingService.cuttingTemplates).value
	.map(t => ({
		key: t.id,
		value: t.name,
	}));
const isAlreadyUsed = ref(props.item.cuttingService.isAlreadyUsed).value;
const isCuttingServiceActive = ref(props.item.cuttingService?.isActive).value;
let activeOption = ref(cuttingTemplates[0]).value?.key;
const state = reactive({
	isCuttingServiceActive,
});
const payload = {
	code: props.item.code,
	cuttingTemplateId: activeOption,
	isActive: isCuttingServiceActive,
};

let cuttingTemplatesNumber = computed(() => {
	return cuttingTemplates.length;
});

const selectCuttingServiceCheckboxAriaLabel = computed(() => {
	return props.accessibilityLabels?.selectCuttingService?.replace('$product$', props.item.name) ?? props.item.name;
});

async function singleSelectUpdate(value) {
	if (activeOption !== value) {
		activeOption = value;
		payload.cuttingTemplateId = value;
		await updateCuttingService(payload);
	}
}

async function checkboxUpdate(event) {
	state.isCuttingServiceActive = event.target.checked;
	payload.isActive = event.target.checked;
	await updateCuttingService(payload);
}
</script>

<template>
	<div class="c-cutting-template__wrapper">
		<div v-if="isAlreadyUsed">
			<div class="c-cutting-template__info-box c-cutting-template__info-box__confirm" data-testid="info-box-active">
				<DsIcon icon-name="checkmark" size="sm" />
				<DsText>{{ labels.activeLabel }}</DsText>
			</div>
		</div>
		<div v-if="!isAlreadyUsed" class="c-cutting-template__checkbox">
			<div class="c-global-checkout__save-profile">
				<input type="checkbox" :id="`add-to-basket-option-cutting-service-${item.code}`" @change="checkboxUpdate"
					:checked="isCuttingServiceActive" data-testid="checkbox-input"
					:aria-label="selectCuttingServiceCheckboxAriaLabel"/>
				<label :for="`add-to-basket-option-cutting-service-${item.code}`" data-testid="checkbox-label">
					{{ labels.checkboxLabel }}
				</label>
				<p v-if="cuttingTemplatesNumber >= 1" v-html="labels.learnMoreLabel"
					class="c-cutting-template__checkbox__learn-more" />
			</div>
			<div v-if="cuttingTemplatesNumber === 0">
				<div v-show="state.isCuttingServiceActive" class="c-cutting-template__info-box c-cutting-template__info-box__info" data-testid="info-box-new">
					<DsIcon icon-name="information-circle" size="sm" />
					<span v-html="labels.newOrderLabel" />
				</div>
			</div>
		</div>
		<div v-if="cuttingTemplatesNumber >= 2 && state.isCuttingServiceActive">
			<dropdown-single-select :label="labels.dropdownLabel" :options="cuttingTemplates"
				:pre-selected="cuttingTemplates[0].key" @keyChosen="singleSelectUpdate">
			</dropdown-single-select>
		</div>
	</div>
</template>

<style lang="scss">
@media (max-width: 640px) {
	.c-cutting-template__wrapper .dropdown {
		display: block;
	}
}
</style>

<style lang="scss" scoped>
@import '../../styles/settings/settings';
@import '../../styles/tools/tools';

.c-cutting-template {

	&__wrapper {
		display: flex;
		flex-direction: column;
		width: 100%;
		line-height: t-rem(24px);

		a {
			color: ds-color('fg', 'link');
			text-decoration: underline;
			font-weight: ds-font-weight("medium");
			cursor: pointer;

			&:hover {
				color: ds-color('fg', 'link-hover');
			}
		}
	}

	&__info-box {
		display: flex;
		flex-direction: row;
		border-radius: 8px;
		padding: ds-space(6);
		margin-top: 20px;

		.ds-icon {
			margin: 0 ds-space(6) 0 0;
		}
	}

	&__info-box__confirm {
		background-color: ds-color('bg', 'status-neutral-alt');
		margin-bottom: ds-space(5);
	}

	&__info-box__info {
		background-color: ds-color('bg', 'status-highlight');
	}

	&__checkbox {
		display: flex;
		flex-direction: column;

		.c-global-checkout__save-profile {
			padding: ds-space(7) 0 0;
			min-height: 48px;
			overflow: visible;

			input:checked+label:after {
				right: 12px;
				width: 13px;
				height: 7px;
				top: 12px;
			}
			input:focus-visible+label::before {
				outline: 4px solid var(--ds-color-focus-default);
			}

			input:checked+label:before {
				border-color: ds-color('border', 'interactive');
			}

		}

		label {
			font-size: t-rem(16px);
			padding: 0 0 0 44px;

			&::before {
				margin-right: 14px;
				width: 24px;
				height: 24px;
				border-color: ds-color('border', 'strong');
				left: 1px;
			}

			&::after {
				left: 6px;
			}
		}

		&__learn-more {
			display: block;
			font-size: t-rem(14px);
			margin: ds-space(3) 0 0 44px;
			font-weight: ds-font-weight("medium");
		}
	}
}
</style>
